import { type StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { type NativeMap } from '@/utils/collections'

import { type State } from '.'
import { type OpenedTab, type TabSnapshot } from './types'

export const EMPTY_TAB_SNAPSHOT: TabSnapshot = {
	openedTabs: [],
	selectedId: undefined,
	selectedTab: undefined,
	temporaryTab: undefined,
}

export const makeTabContextKey = (
	systemId: string | null | undefined,
	branchId: string | number | null | undefined,
): string | null => {
	if (!systemId) return null
	return `${systemId}:${branchId == null || branchId === '' ? 'none' : branchId}`
}

export const pickTabSnapshot = (state: TabSnapshot): TabSnapshot => ({
	openedTabs: state.openedTabs.map((tab) => ({ ...tab })),
	selectedId: state.selectedId,
	selectedTab: state.selectedTab,
	temporaryTab: state.temporaryTab,
})

export const tabContextSystemId = (contextKey: string | null | undefined) =>
	contextKey?.split(':', 1)[0] || null

export const tabBelongsToContext = (
	tab: Pick<OpenedTab, 'systemNodeId'>,
	contextKey: string | null | undefined,
) => {
	const systemId = tabContextSystemId(contextKey)
	return systemId == null || String(tab.systemNodeId) === systemId
}

export const filterTabSnapshotForContext = (
	snapshot: TabSnapshot,
	contextKey: string,
): TabSnapshot => {
	const kept = snapshot.openedTabs
		.map((tab, index) => ({ index, tab }))
		.filter(({ tab }) => tabBelongsToContext(tab, contextKey))
	const remapIndex = (index: number | undefined) => {
		if (index === undefined) return undefined
		const mapped = kept.findIndex((entry) => entry.index === index)
		return mapped < 0 ? undefined : mapped
	}
	const openedTabs = kept.map(({ tab }) => ({ ...tab }))
	const selectedTab = remapIndex(snapshot.selectedTab)

	return {
		openedTabs,
		selectedId:
			selectedTab === undefined
				? undefined
				: openedTabs[selectedTab]?.nodeId,
		selectedTab,
		temporaryTab: remapIndex(snapshot.temporaryTab),
	}
}

type StoredTabSession = TabSnapshot & {
	contextKey?: string | null
	contexts?: Record<string, TabSnapshot>
}

/**
 * Selects only the snapshot belonging to the active system/branch. The top-level
 * snapshot is used solely when it identifies the same context, or for a legacy
 * pre-context session (which is still filtered by system).
 */
export const resolveTabSnapshotForContext = (
	session: StoredTabSession | null | undefined,
	contextKey: string,
): TabSnapshot => {
	if (!session) return pickTabSnapshot(EMPTY_TAB_SNAPSHOT)
	const stored = session.contexts?.[contextKey]
	if (stored) return filterTabSnapshotForContext(stored, contextKey)
	if (session.contextKey === contextKey) {
		return filterTabSnapshotForContext(session, contextKey)
	}
	const isLegacy =
		session.contextKey == null &&
		Object.keys(session.contexts ?? {}).length === 0
	if (isLegacy) return filterTabSnapshotForContext(session, contextKey)
	return pickTabSnapshot(EMPTY_TAB_SNAPSHOT)
}

export const closeDeletedTabs = (
	node: StructureDto,
	children: NativeMap<number[]>,
	state: State,
) => {
	const removedIndexes: number[] = []
	const removedIds: number[] = []

	const addNode = (id: number | undefined) => {
		if (!id) return
		removedIds.push(id)
		;(children[id] || []).forEach((c) => addNode(c))
	}

	const clearIfRemoved = (index: number | undefined) => {
		if (index === undefined) {
			return undefined
		}

		if (removedIndexes.includes(index)) {
			return undefined
		}

		const indexShift = removedIndexes.filter((x) => x < index).length

		return index - indexShift
	}

	addNode(node?.id)

	const openedTabs = state.openedTabs.reduce((acc, tab, index) => {
		if (removedIds.includes(tab.nodeId)) {
			removedIndexes.push(index)
		} else {
			acc.push({ ...tab })
		}

		return acc
	}, [] as OpenedTab[])

	return {
		openedTabs,
		selectedTab: clearIfRemoved(state.selectedTab),
		temporaryTab: clearIfRemoved(state.temporaryTab),
	}
}
